import React from 'react';
import { Container, Typography, Box, Card, Grid, TextField, Button, useTheme, Divider, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Email, LocationOn, Support, Send, Business, Person, ContactSupport } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ContactUs = () => {
  const theme = useTheme();

  const contactMethods = [
    {
      icon: <Email sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Email Support",
      description: "Send us an email for general inquiries and support",
      contact: "info@seethakalyanavaibhogame.com ",
      response: "Response within 24-48 hours"
    },
    {
      icon: <Business sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Website Support",
      description: "Contact us through our website for immediate assistance",
      contact: "seethakalyanavaibhogame.com",
      response: "Available 24/7"
    },
    {
      icon: <LocationOn sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      title: "Office Address",
      description: "Visit our office for in-person support",
      contact: "29-1-26, Flat No 101, Papa Heights, Sheshadri Sastry Street, Governor Pet, Vijayawada (Urban), Andhra Pradesh - 520002",
      response: "By appointment only"
    }
  ];

  const grievanceOfficer = {
    name: "Sigirisetty Adiyya",
    title: "Grievance Officer",
    address: "29-1-26, Flat No 101, Papa Heights, Sheshadri Sastry Street, Governor Pet, Vijayawada (Urban), Andhra Pradesh - 520002"
  };

  const contactReasons = [
    {
      icon: <ContactSupport sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Privacy Statement",
      description: "Questions about our privacy policy and data protection practices"
    },
    {
      icon: <Business sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Website Practices",
      description: "Inquiries about how our website operates and our services"
    },
    {
      icon: <Person sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: "Account Issues",
      description: "Help with your account, profile, or membership"
    }
  ];

  const faqItems = [
    {
      question: "How do I contact seethakalyanavaibhogame.com?",
      answer: "You can contact us via email at info@seethakalyanavaibhogame.com  or visit our office at the address provided. For urgent matters, please email us directly."
    },
    {
      question: "What is the role of the Grievance Officer?",
      answer: "The Grievance Officer handles complaints and concerns about privacy, website practices, and user dealings with our platform."
    },
    {
      question: "How quickly will I receive a response?",
      answer: "We aim to respond to all inquiries within 24-48 hours. For urgent matters, please clearly indicate the urgency in your email."
    },
    {
      question: "Can I visit the office in person?",
      answer: "Yes, you can visit our office at the provided address, but we recommend scheduling an appointment first for better assistance."
    },
    {
      question: "What information should I include in my inquiry?",
      answer: "Please include your name, contact details, and a clear description of your question or concern for faster resolution."
    }
  ];

  return (
    <Box sx={{ bgcolor: theme.palette.background.default, minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box sx={{ 
        py: 10,
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box
          component="img"
          src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2000&q=80"
          alt="Background"
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.1,
            top: 0,
            left: 0
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Support sx={{ fontSize: 80, color: 'white' }} />
            </Box>
            <Typography 
              variant="h1" 
              sx={{ 
                fontWeight: 900,
                fontSize: { xs: '2.5rem', md: '4rem' },
                mb: 3,
                textShadow: '0 2px 4px rgba(0,0,0,0.3)'
              }}
            >
              Contact Us
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                maxWidth: '800px',
                mx: 'auto',
                lineHeight: 1.6,
                opacity: 0.9,
                mb: 4
              }}
            >
              Tell me how to contact seethakalyanavaibhogame.com
            </Typography>
            <Chip 
              label="We're here to help!"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '1.1rem',
                fontWeight: 600,
                px: 3,
                py: 1
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Main Contact Information */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Card sx={{ 
            p: 6,
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center',
            mb: 6
          }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                mb: 4
              }}
            >
              Get in Touch
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                lineHeight: 1.6,
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                mb: 4
              }}
            >
              If you have any questions about this privacy statement, the practices of this site, 
              or your dealings with this website, please email us or contact us at the following address.
            </Typography>
            <Chip 
              label="Professional Support Team"
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.2)',
                color: 'white',
                fontSize: '1.2rem',
                fontWeight: 700,
                px: 4,
                py: 2
              }}
            />
          </Card>
        </Container>
      </Box>

      {/* Contact Methods */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Contact Methods
          </Typography>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            {contactMethods.map((method, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ minWidth: 370, maxWidth: 370 }}>
                <Card sx={{ 
                  p: 0,
                  pt: 0,
                  textAlign: 'center',
                  height: '320px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  borderRadius: '22px',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
                  border: 'none',
                  position: 'relative',
                  overflow: 'visible',
                  transition: 'all 0.3s cubic-bezier(.4,2,.6,1)',
                  '&:hover': {
                    transform: 'translateY(-10px) scale(1.03)',
                    boxShadow: '0 20px 50px rgba(0,0,0,0.13)'
                  },
                  '&::before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '7px',
                    borderTopLeftRadius: '22px',
                    borderTopRightRadius: '22px',
                    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                    zIndex: 2
                  }
                }}>
                  <Box sx={{
                    mt: 4,
                    mb: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                    mx: 'auto',
                    position: 'relative',
                    zIndex: 3
                  }}>
                    {React.cloneElement(method.icon, { sx: { fontSize: 38, color: 'white' } })}
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 800,
                      color: theme.palette.primary.main,
                      mb: 1.5,
                      fontSize: '1.35rem',
                      mt: 1
                    }}
                  >
                    {method.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      lineHeight: 1.7,
                      mb: 2,
                      fontSize: '1.05rem',
                      px: 2
                    }}
                  >
                    {method.description}
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      mb: 0.5,
                      fontSize: '1.08rem',
                      wordBreak: 'break-all'
                    }}
                  >
                    {method.contact}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      fontSize: '0.98rem'
                    }}
                  >
                    {method.response}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Grievance Officer */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Grievance Officer
          </Typography>
          <Card sx={{ 
            p: 6,
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: `3px solid ${theme.palette.primary.main}`,
            background: `linear-gradient(135deg, rgba(156,39,176,0.05) 0%, rgba(103,58,183,0.05) 100%)`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
              <Person sx={{ fontSize: 60, color: theme.palette.primary.main, mr: 3 }} />
              <Box>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 700,
                    color: theme.palette.primary.main
                  }}
                >
                  {grievanceOfficer.name}
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontWeight: 600
                  }}
                >
                  {grievanceOfficer.title}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 4 }} />
            <Typography 
              variant="h6" 
              sx={{ 
                color: theme.palette.primary.main,
                fontWeight: 600,
                mb: 2
              }}
            >
              Office Address:
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: theme.palette.text.secondary,
                lineHeight: 1.8,
                fontSize: '1.1rem',
                fontWeight: 500
              }}
            >
              {grievanceOfficer.address}
            </Typography>
          </Card>
        </Container>
      </Box>

      {/* Contact Reasons */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Why Contact Us?
          </Typography>
          <Grid container spacing={4} wrap="nowrap" sx={{ overflowX: 'auto', pb: 2 }}>
            {contactReasons.map((reason, index) => (
              <Grid item xs={12} md={4} key={index} sx={{ minWidth: 320, maxWidth: 320 }}>
                <Card sx={{ 
                  p: 4,
                  textAlign: 'center',
                  height: '260px',
                  transition: 'all 0.3s ease',
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.1)'
                  }
                }}>
                  <Box sx={{ mb: 3 }}>
                    {reason.icon}
                  </Box>
                  <Typography 
                    variant="h5" 
                    sx={{ 
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                      mb: 2
                    }}
                  >
                    {reason.title}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      lineHeight: 1.6
                    }}
                  >
                    {reason.description}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="md">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Send Us a Message
          </Typography>
          <Card sx={{ 
            p: 6, 
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: `2px solid ${theme.palette.primary.main}`
          }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  variant="outlined"
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  variant="outlined"
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  variant="outlined"
                  type="email"
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  variant="outlined"
                  type="tel"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subject"
                  variant="outlined"
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Message"
                  variant="outlined"
                  multiline
                  rows={4}
                  required
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                />
              </Grid>
              <Grid item xs={12} sx={{ textAlign: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Send />}
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    px: 6,
                    py: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: '50px',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark,
                      transform: 'translateY(-2px)',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  Send Message
                </Button>
              </Grid>
            </Grid>
          </Card>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: theme.palette.background.paper }}>
        <Container maxWidth="lg">
          <Typography 
            variant="h3" 
            sx={{ 
              textAlign: 'center',
              mb: 6,
              fontWeight: 700,
              color: theme.palette.primary.main
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {faqItems.map((faq, index) => (
              <Accordion key={index} sx={{
                mb: 2,
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                border: `1.5px solid ${theme.palette.primary.light}`,
                '&:before': { display: 'none' },
                background: 'white'
              }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                  aria-controls={`faq-content-${index}`}
                  id={`faq-header-${index}`}
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    fontSize: '1.1rem',
                    px: 3,
                    py: 2
                  }}
                >
                  {faq.question}
                </AccordionSummary>
                <AccordionDetails sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '1rem',
                  px: 3,
                  py: 2
                }}>
                  {faq.answer}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Additional Information */}
      <Box sx={{ py: 10 }}>
        <Container maxWidth="lg">
          <Card sx={{ 
            p: 6,
            borderRadius: '20px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            color: 'white',
            textAlign: 'center'
          }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 700,
                mb: 4
              }}
            >
              Professional Support
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                lineHeight: 1.8,
                opacity: 0.9,
                maxWidth: '800px',
                mx: 'auto',
                mb: 4
              }}
            >
              Our dedicated team is committed to providing you with the best possible support. 
              Whether you have questions about our privacy policy, website practices, or need assistance 
              with your account, we're here to help you every step of the way.
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                opacity: 0.8,
                fontStyle: 'italic'
              }}
            >
              Your satisfaction and trust are our top priorities.
            </Typography>
          </Card>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactUs; 